
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { useSession } from "@/hooks/useSession";
import { getMaxResources } from "@/utils/subscriptionLimits";
import { CourseLibraryStatus } from "./CourseLibraryStatus";
import { CourseTemplateGrid } from "./CourseTemplateGrid";
import { TemplateService } from "@/services/template/TemplateService";
import type { SubscriptionTier } from "@/hooks/useProfile";

interface CourseTemplate {
  id: string;
  title: string;
  description: string | null;
  preview_image_url: string | null;
  topic: string;
  company: string;
  primary_color: string;
  secondary_color: string;
}

export function CourseLibraryDialog({ 
  open, 
  onOpenChange 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const { session } = useSession();
  const { data: profile } = useProfile(session?.user?.id);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['course-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('course_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CourseTemplate[];
    }
  });

  const subscriptionTier = (profile?.subscription_tier === 'trainee' ? 'free' : profile?.subscription_tier) as SubscriptionTier;
  const maxResources = profile ? getMaxResources(subscriptionTier) : 0;
  const resourcesUsed = profile?.resources_used_this_month || 0;
  const canCreateMore = resourcesUsed < maxResources;
  const isInactive = profile?.billing_status === 'inactive';

  const handleTemplateSelect = async (template: CourseTemplate) => {
    if (isInactive) {
      toast({
        title: "Account Inactive",
        description: "Your account is currently inactive. Please update your billing information to continue.",
        variant: "destructive",
      });
      return;
    }

    if (!canCreateMore) {
      toast({
        title: "Resource limit reached",
        description: "Please upgrade your plan to create more resources.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreating(true);
      
      await TemplateService.createFromTemplate({
        templateId: template.id,
        userId: session?.user?.id!,
        topic: template.topic,
        company: template.company,
        primaryColor: template.primary_color,
        secondaryColor: template.secondary_color,
        previewImageUrl: template.preview_image_url
      });

      toast({
        title: "Success",
        description: "Course template has been added to your resources.",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error creating resource from template:', error);
      toast({
        title: "Error",
        description: "Failed to create resource from template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-xl">Course Library</DialogTitle>
        </DialogHeader>

        <CourseLibraryStatus
          isInactive={isInactive}
          resourcesUsed={resourcesUsed}
          maxResources={maxResources}
          canCreateMore={canCreateMore}
        />

        <CourseTemplateGrid
          templates={templates}
          isLoading={isLoading}
          onSelect={handleTemplateSelect}
          disabled={isCreating || !canCreateMore || isInactive}
        />
      </DialogContent>
    </Dialog>
  );
}
